import React, { useState, useEffect, useMemo } from "react";
import { useContext } from "react";
import { DataContext } from "../contexts/DataContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faChevronLeft,
  faChevronRight,
  faSpinner,
  faExclamationTriangle,
  faClock,
  faCheck,
  faShip,
  faTruck,
  faPlane,
  faQuestion,
  faInfoCircle,
  faFilter,
  faSearch,
  faTimes,
  faEye,
  faDownload,
  faFileExcel,
  faClipboardList,
  faSync,
  faLayerGroup,
  faChartBar,
  faChartPie,
  faTable,
  faChartLine,
  faFileInvoice,
  faIdCard,
  faTag,
  faColumns,
  faExpand,
  faCompress,
} from "@fortawesome/free-solid-svg-icons";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";

// Import custom components
import TransportTypeDistributionChart from "./TransportTypeDistributionChart";
import StatusDistributionChart from "./StatusDistributionChart";
import ShipmentsByWeekdayChart from "./ShipmentsByWeekdayChart";
import ShipmentDetailModal from "./ShipmentDetailModal";
import CalendarMonthView from "./CalendarMonthView";
import CalendarWeekView from "./CalendarWeekView";
import CalendarDayView from "./CalendarDayView";

const EnhancedTrafficScheduler = () => {
  const {
    trafficData: rawTrafficData,
    isLoading,
    fetchTrafficData,
    setShouldFetchData,
  } = useContext(DataContext);

  // Ensure trafficData is always a valid array
  const trafficData = useMemo(() => {
    if (!rawTrafficData) return [];
    if (!Array.isArray(rawTrafficData)) return [];

    // Filter out any invalid items
    return rawTrafficData.filter((item) => item && item.header);
  }, [rawTrafficData]);

  // State for calendar view
  const [viewType, setViewType] = useState("month"); // "month", "week", "day"
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState([]);
  const [hoveredReference, setHoveredReference] = useState(null);
  const [selectedReference, setSelectedReference] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  // State for layout and UI control
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [chartLayout, setChartLayout] = useState({
    columns: 3, // Default to 3 columns for charts
    size: "medium", // Options: small, medium, large
  });

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    customerIds: [],
    status: [],
    transportTypes: [],
    searchTerm: "",
  });

  // Stats state
  const [stats, setStats] = useState({
    total: 0,
    delayed: 0,
    onTime: 0,
    critical: 0,
    upcoming: 0,
    byTransportType: [],
    byStatus: [],
    byWeekday: [],
  });

  // Process traffic data when it's available
  useEffect(() => {
    console.log("TrafficData in scheduler:", trafficData);
    if (!trafficData || trafficData.length === 0) return;

    // Check for any undefined or malformed items
    const invalidItems = trafficData.filter((item) => !item || !item.header);
    if (invalidItems.length > 0) {
      console.error("Found invalid items in trafficData:", invalidItems);
    }

    processTrafficData();
  }, [trafficData, selectedDate, viewType, filters]);

  // Trigger data fetch when component mounts
  useEffect(() => {
    // Correctly update the shouldFetchData object
    setShouldFetchData((prev) => {
      // Make sure you're dealing with an object
      const prevObject = typeof prev === "object" ? prev : {};
      return {
        ...prevObject,
        fetchTrafficData: true,
      };
    });

    return () => {
      setShouldFetchData((prev) => {
        const prevObject = typeof prev === "object" ? prev : {};
        return {
          ...prevObject,
          fetchTrafficData: false,
        };
      });
    };
  }, [setShouldFetchData]);

  // Helper function to check if a date is the same day
  const isSameDay = (date1, date2) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // Helper functions for calendar views
  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const getLastDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  const getFirstDayOfWeek = (date) => {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
    return new Date(date.setDate(diff));
  };

  const getLastDayOfWeek = (date) => {
    const firstDay = getFirstDayOfWeek(new Date(date));
    const lastDay = new Date(firstDay);
    lastDay.setDate(lastDay.getDate() + 6);
    return lastDay;
  };

  // Process traffic data based on current view and filters
  const processTrafficData = () => {
    // Apply filters - with safety checks
    let filtered = trafficData.filter((item) => {
      // Safety check for item and header
      if (!item || !item.header) {
        console.warn("Encountered an item without a header property:", item);
        return false;
      }

      // Filter by customer IDs
      if (
        filters.customerIds.length > 0 &&
        !filters.customerIds.includes(item.header.customer_id)
      ) {
        return false;
      }

      // Filter by status
      if (filters.status.length > 0) {
        const status = item.header.trafficStatus || "N/A";
        if (!filters.status.includes(status)) {
          return false;
        }
      }

      // Filter by transport type
      if (filters.transportTypes.length > 0) {
        const entryPoint = item.header.pedimento?.pedimento_entry_point;
        const transportMap = {
          160: "Sea",
          510: "Sea",
          430: "Sea",
          240: "Road",
          520: "Air",
          521: "Air",
          640: "Air",
        };
        const transport = transportMap[entryPoint] || "Unknown";
        if (!filters.transportTypes.includes(transport)) {
          return false;
        }
      }

      // Filter by search term
      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        const reference = item.header.reference?.toLowerCase() || "";
        const customerName = item.header.customer_name?.toLowerCase() || "";
        const pedimento =
          item.header.pedimento?.pedimento_number?.toLowerCase() || "";

        if (
          !reference.includes(term) &&
          !customerName.includes(term) &&
          !pedimento.includes(term)
        ) {
          return false;
        }
      }

      return true;
    });

    // Set the filtered data state
    setFilteredData(filtered);

    // Process data based on view type
    let startDate, endDate;

    if (viewType === "month") {
      startDate = getFirstDayOfMonth(selectedDate);
      endDate = getLastDayOfMonth(selectedDate);
    } else if (viewType === "week") {
      startDate = getFirstDayOfWeek(selectedDate);
      endDate = getLastDayOfWeek(selectedDate);
    } else {
      // Day view
      startDate = new Date(selectedDate);
      endDate = new Date(selectedDate);
    }

    // Generate calendar days
    const days = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Map shipments to days
    const calendarDays = days.map((day) => {
      const dayShipments = filtered.filter((item) => {
        const etaDate = getEtaDate(item);
        return etaDate && isSameDay(new Date(etaDate), day);
      });

      return {
        date: new Date(day),
        shipments: dayShipments,
      };
    });

    setCalendarData(calendarDays);

    // Calculate statistics
    calculateStatistics(filtered);
  };

  // Helper to get a valid ETA date from an item
  const getEtaDate = (item) => {
    // Safety check
    if (!item || !item.header) return null;

    // First check the ebookingDates.fechaETA
    if (item.header.ebookingDates && item.header.ebookingDates.fechaETA) {
      return item.header.ebookingDates.fechaETA;
    }

    // Check in detail.dates_data if available
    if (
      item.header.detail &&
      item.header.detail.dates_data &&
      item.header.detail.dates_data.date_inbound
    ) {
      return item.header.detail.dates_data.date_inbound;
    }

    return null;
  };

  // Helper to check if a shipment is closed/completed
  const isShipmentClosed = (item) => {
    // Safety check
    if (!item || !item.header) return false;

    // Check trafficStatus field for completion indicators
    const trafficStatus = item.header.trafficStatus;
    if (trafficStatus) {
      const closedStatuses = ['pagado', 'delivered', 'entregado', 'completed', 'cerrado'];
      if (closedStatuses.includes(trafficStatus.toLowerCase())) {
        return true;
      }
    }

    // Check detail.traffic_data.status field
    const detailStatus = item.header.detail?.traffic_data?.status;
    if (detailStatus) {
      const closedStatuses = ['pagado', 'delivered', 'entregado', 'completed', 'cerrado'];
      if (closedStatuses.includes(detailStatus.toLowerCase())) {
        return true;
      }
    }

    // Check if there's a fechaPiso (delivery date)
    if (item.header.ebookingDates && item.header.ebookingDates.fechaPiso) {
      return true;
    }

    // Check if pedimento has been paid (date_pedimento_paid exists)
    if (item.header.detail?.dates_data?.date_pedimento_paid) {
      return true;
    }

    return false;
  };

  // Helper to check if a shipment is delayed
  const isShipmentDelayed = (item) => {
    const etaDate = getEtaDate(item);
    if (!etaDate) return false;

    // Don't consider closed shipments as delayed
    if (isShipmentClosed(item)) return false;

    const today = new Date();
    // Check if ETA date is in the past
    return new Date(etaDate) < today;
  };

  // Helper to check if a shipment is within the next week
  const isShipmentUpcoming = (item) => {
    const etaDate = getEtaDate(item);
    if (!etaDate) return false;

    // Don't consider closed shipments as upcoming
    if (isShipmentClosed(item)) return false;

    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);

    return new Date(etaDate) > today && new Date(etaDate) <= nextWeek;
  };

  // Helper to check if a shipment is critical (delayed over 3 days)
  const isShipmentCritical = (item) => {
    const etaDate = getEtaDate(item);
    if (!etaDate) return false;

    // Don't consider closed shipments as critical
    if (isShipmentClosed(item)) return false;

    const today = new Date();
    const threeDaysAgo = new Date(today);
    threeDaysAgo.setDate(today.getDate() - 3);

    return new Date(etaDate) < threeDaysAgo;
  };

  // Calculate statistics for dashboard
  const calculateStatistics = (data) => {
    // Count by status
    const completed = data.filter((item) => isShipmentClosed(item)).length;
    const delayed = data.filter((item) => isShipmentDelayed(item)).length;
    const upcoming = data.filter((item) => isShipmentUpcoming(item)).length;
    const critical = data.filter((item) => isShipmentCritical(item)).length;

    // Calculate on time as remaining active shipments (not closed, delayed, or critical)
    const activeShipments = data.filter((item) => !isShipmentClosed(item));
    const onTime = activeShipments.length - delayed - critical;

    // Count by transport type
    const transportCounts = {};
    data.forEach((item) => {
      // Safety check
      if (!item || !item.header) return;

      const entryPoint = item.header.pedimento?.pedimento_entry_point;
      const transportMap = {
        160: "Sea",
        510: "Sea",
        430: "Sea",
        240: "Road",
        520: "Air",
        521: "Air",
        640: "Air",
      };
      const transport = transportMap[entryPoint] || "Unknown";

      transportCounts[transport] = (transportCounts[transport] || 0) + 1;
    });

    // Transform transport counts to array for chart
    const byTransportType = Object.keys(transportCounts).map((key) => ({
      name: key,
      value: transportCounts[key],
    }));

    // Count by status
    const statusCounts = {};
    data.forEach((item) => {
      // Safety check
      if (!item || !item.header) return;

      const status = item.header.trafficStatus || "N/A";
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    // Transform status counts to array for chart
    const byStatus = Object.keys(statusCounts).map((key) => ({
      name: key,
      value: statusCounts[key],
    }));

    // Count shipments by weekday
    const weekdayCounts = [0, 0, 0, 0, 0, 0, 0]; // Sun, Mon, ..., Sat
    const weekdayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    data.forEach((item) => {
      const etaDate = getEtaDate(item);
      if (etaDate) {
        const date = new Date(etaDate);
        const weekday = date.getDay();
        weekdayCounts[weekday]++;
      }
    });

    const byWeekday = weekdayCounts.map((count, index) => ({
      name: weekdayNames[index],
      count,
    }));

    setStats({
      total: data.length,
      completed,
      delayed,
      onTime,
      critical,
      upcoming,
      byTransportType,
      byStatus,
      byWeekday,
    });
  };

  // Navigate to previous time period
  const goToPrevious = () => {
    const newDate = new Date(selectedDate);

    if (viewType === "month") {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (viewType === "week") {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }

    setSelectedDate(newDate);
  };

  // Navigate to next time period
  const goToNext = () => {
    const newDate = new Date(selectedDate);

    if (viewType === "month") {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (viewType === "week") {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }

    setSelectedDate(newDate);
  };

  // Format date for display
  const formatDate = (date) => {
    if (viewType === "month") {
      return date.toLocaleDateString([], { month: "long", year: "numeric" });
    } else if (viewType === "week") {
      const firstDay = getFirstDayOfWeek(new Date(date));
      const lastDay = getLastDayOfWeek(new Date(date));
      return `${firstDay.toLocaleDateString([], {
        month: "short",
        day: "numeric",
      })} - ${lastDay.toLocaleDateString([], {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}`;
    } else {
      return date.toLocaleDateString([], {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
  };

  // Get background color for shipment based on status
  const getShipmentBackgroundColor = (item) => {
    if (isShipmentCritical(item)) {
      return "bg-red-100";
    } else if (isShipmentDelayed(item)) {
      return "bg-amber-100";
    } else if (isShipmentUpcoming(item)) {
      return "bg-blue-100";
    } else {
      return "bg-green-50";
    }
  };

  // Get status text for a shipment
  const getShipmentStatusText = (item) => {
    if (isShipmentCritical(item)) {
      return "Critical Delay";
    } else if (isShipmentDelayed(item)) {
      return "Delayed";
    } else if (isShipmentUpcoming(item)) {
      return "Upcoming";
    } else {
      return "On Time";
    }
  };

  // Get icon for transport type
  const getTransportIcon = (entryPoint) => {
    if (!entryPoint)
      return {
        icon: faQuestion,
        color: "text-gray-500",
        title: "Unknown Transport Type",
      };

    const transportIconsMap = {
      // Seafreight
      160: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },
      510: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },
      430: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },

      // Roadfreight
      240: { icon: faTruck, color: "text-green-600", title: "Road Freight" },

      // Airfreight
      520: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },
      521: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },
      640: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },

      // Default for unknown entry points
      default: {
        icon: faQuestion,
        color: "text-gray-500",
        title: "Unknown Transport Type",
      },
    };

    // Convert to number if it's a string
    const entryPointNum =
      typeof entryPoint === "string" ? parseInt(entryPoint, 10) : entryPoint;
    return transportIconsMap[entryPointNum] || transportIconsMap["default"];
  };

  // Handle shipment selection
  const handleShipmentClick = (item) => {
    setSelectedReference(item);
    setShowDetailModal(true);
  };

  // Apply a filter
  const applyFilter = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      customerIds: [],
      status: [],
      transportTypes: [],
      searchTerm: "",
    });
  };

  // Generate customer options for filters
  const customerOptions = useMemo(() => {
    if (!trafficData) return [];

    const uniqueCustomers = new Map();

    trafficData.forEach((item) => {
      // Safety check
      if (!item || !item.header) return;

      if (item.header.customer_id && item.header.customer_name) {
        uniqueCustomers.set(item.header.customer_id, item.header.customer_name);
      }
    });

    return Array.from(uniqueCustomers).map(([id, name]) => ({
      value: id,
      label: `${name} (${id})`,
    }));
  }, [trafficData]);

  // Generate status options for filters - FIXED HERE
  const statusOptions = useMemo(() => {
    if (!trafficData) return [];

    const statuses = new Set();

    trafficData.forEach((item) => {
      // Safety check - this was missing
      if (!item || !item.header) return;

      const status = item.header.trafficStatus || "N/A";
      statuses.add(status);
    });

    return Array.from(statuses).map((status) => ({
      value: status,
      label: status,
    }));
  }, [trafficData]);

  // Generate transport type options for filters
  const transportOptions = [
    { value: "Sea", label: "Sea Freight" },
    { value: "Air", label: "Air Freight" },
    { value: "Road", label: "Road Freight" },
    { value: "Unknown", label: "Unknown" },
  ];

  // Toggle chart layout
  const toggleChartLayout = () => {
    setChartLayout((prev) => ({
      ...prev,
      columns: prev.columns === 3 ? 2 : 3, // Toggle between 2 and 3 columns
    }));
  };

  // Toggle chart size
  const toggleChartSize = () => {
    const sizes = ["small", "medium", "large"];
    const currentIndex = sizes.indexOf(chartLayout.size);
    const nextIndex = (currentIndex + 1) % sizes.length;

    setChartLayout((prev) => ({
      ...prev,
      size: sizes[nextIndex],
    }));
  };

  // Handle data refresh
  const handleRefreshData = () => {
    setShouldFetchData((prev) => ({
      ...prev,
      fetchTrafficData: true,
    }));
    fetchTrafficData();
  };

  // Get chart size classes based on layout settings
  const getChartSizeClass = () => {
    const columnClasses = {
      1: "grid-cols-1",
      2: "grid-cols-1 lg:grid-cols-2",
      3: "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
    };

    const sizeClasses = {
      small: "h-64",
      medium: "h-80",
      large: "h-96",
    };

    return {
      columns: columnClasses[chartLayout.columns],
      height: sizeClasses[chartLayout.size],
    };
  };

  // Custom select styles for filters
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      "&:hover": {
        borderColor: "#cbd5e1",
      },
      borderRadius: "0.375rem",
      minHeight: "38px",
      transition: "all 0.2s ease",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#e1effe",
      borderRadius: "0.25rem",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#1e40af",
      fontWeight: 500,
      fontSize: "0.8125rem",
      padding: "0.125rem 0.25rem",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#60a5fa",
      ":hover": {
        backgroundColor: "#bfdbfe",
        color: "#1e3a8a",
      },
      borderRadius: "0 0.25rem 0.25rem 0",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8",
      fontSize: "0.875rem",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: "0.375rem",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      border: "1px solid #e2e8f0",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "#e1effe"
        : "white",
      color: state.isSelected ? "white" : "#1e293b",
      fontSize: "0.875rem",
      padding: "0.5rem 0.75rem",
      ":active": {
        backgroundColor: state.isSelected ? "#3b82f6" : "#bfdbfe",
      },
    }),
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.customerIds.length > 0 ||
    filters.status.length > 0 ||
    filters.transportTypes.length > 0 ||
    filters.searchTerm.length > 0;

  // Render loading spinner when data is loading
  if (isLoading && trafficData.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-3xl font-semibold animate-spin text-blue-600 mr-3"
        />
        <div className="text-2xl font-semibold">Loading Scheduler...</div>
      </div>
    );
  }

  // Handle case where there's no data after loading
  if (!isLoading && trafficData.length === 0) {
    return (
      <div className="bg-gray-50 min-h-screen flex flex-col">
        <div className="flex justify-center items-center h-screen">
          <div className="text-xl text-gray-600">No traffic data available</div>
        </div>
      </div>
    );
  }

  // Get chart size styling
  const chartSizeClasses = getChartSizeClass();

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      {/* Header with title and navigation */}
      <div className="w-full bg-white shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-[#1A2654] to-blue-700 text-white">
          {/* Main header with title and action buttons */}
          <div className="flex justify-between items-center px-6 pt-5 pb-6">
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-2xl" />
              <h1 className="text-2xl font-bold tracking-tight">
                Traffic Scheduler Dashboard
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefreshData}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                disabled={isLoading}
              >
                <FontAwesomeIcon
                  icon={isLoading ? faSpinner : faSync}
                  className={isLoading ? "animate-spin" : ""}
                />
                <span>{isLoading ? "Refreshing..." : "Refresh Data"}</span>
              </button>
              <button
                onClick={toggleChartLayout}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faColumns} />
                <span>{`${chartLayout.columns} Columns`}</span>
              </button>
              <button
                onClick={toggleChartSize}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
              >
                <FontAwesomeIcon
                  icon={chartLayout.size === "large" ? faCompress : faExpand}
                />
                <span>{`${
                  chartLayout.size.charAt(0).toUpperCase() +
                  chartLayout.size.slice(1)
                } Size`}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-6">
        {/* Calendar Controls */}
        <div className="mb-6">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4">
              <div className="flex flex-col md:flex-row gap-3 items-center">
                {/* Date Navigation */}
                <div className="flex items-center space-x-3">
                  <button
                    onClick={goToPrevious}
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>

                  <div className="relative">
                    <div className="flex items-center space-x-2 bg-white p-2 border rounded cursor-pointer">
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="text-gray-500"
                      />
                      <span className="font-medium">
                        {formatDate(selectedDate)}
                      </span>
                    </div>
                    <DatePicker
                      selected={selectedDate}
                      onChange={(date) => setSelectedDate(date)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>

                  <button
                    onClick={goToNext}
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>

                {/* View type selector */}
                <div
                  className="inline-flex rounded-md shadow-sm mx-4"
                  role="group"
                >
                  <button
                    type="button"
                    onClick={() => setViewType("month")}
                    className={`px-4 py-2 text-sm font-medium ${
                      viewType === "month"
                        ? "bg-blue-700 text-white"
                        : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                    } rounded-l-lg`}
                  >
                    Month
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewType("week")}
                    className={`px-4 py-2 text-sm font-medium ${
                      viewType === "week"
                        ? "bg-blue-700 text-white"
                        : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    Week
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewType("day")}
                    className={`px-4 py-2 text-sm font-medium ${
                      viewType === "day"
                        ? "bg-blue-700 text-white"
                        : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                    } rounded-r-lg`}
                  >
                    Day
                  </button>
                </div>

                {/* Filter and search */}
                <div className="flex flex-wrap gap-2 justify-end ml-auto">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      showFilters || hasActiveFilters
                        ? "bg-blue-100 text-blue-700 border border-blue-200"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faFilter} />
                    <span>Filters</span>
                    {hasActiveFilters && (
                      <span className="ml-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {filters.customerIds.length +
                          filters.status.length +
                          filters.transportTypes.length +
                          (filters.searchTerm ? 1 : 0)}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={resetFilters}
                    disabled={!hasActiveFilters}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      hasActiveFilters
                        ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                        : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                    }`}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    <span>Clear Filters</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Customer Filter */}
                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faFileInvoice}
                        className="mr-2 text-blue-600"
                      />
                      Customer
                    </label>
                    <Select
                      options={customerOptions}
                      isMulti
                      onChange={(selected) =>
                        applyFilter(
                          "customerIds",
                          selected.map((option) => option.value)
                        )
                      }
                      value={customerOptions.filter((option) =>
                        filters.customerIds.includes(option.value)
                      )}
                      placeholder="Select Customers"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  {/* Status Filter */}
                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faTag}
                        className="mr-2 text-blue-600"
                      />
                      Status
                    </label>
                    <Select
                      options={statusOptions}
                      isMulti
                      onChange={(selected) =>
                        applyFilter(
                          "status",
                          selected.map((option) => option.value)
                        )
                      }
                      value={statusOptions.filter((option) =>
                        filters.status.includes(option.value)
                      )}
                      placeholder="Select Status"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  {/* Transport Type Filter */}
                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faShip}
                        className="mr-2 text-blue-600"
                      />
                      Transport Type
                    </label>
                    <Select
                      options={transportOptions}
                      isMulti
                      onChange={(selected) =>
                        applyFilter(
                          "transportTypes",
                          selected.map((option) => option.value)
                        )
                      }
                      value={transportOptions.filter((option) =>
                        filters.transportTypes.includes(option.value)
                      )}
                      placeholder="Select Transport Types"
                      classNamePrefix="select-filter"
                      className="text-sm"
                      styles={customSelectStyles}
                    />
                  </div>

                  {/* Search Input */}
                  <div className="filter-group">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="mr-2 text-blue-600"
                      />
                      Search
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Search references, customers..."
                        value={filters.searchTerm}
                        onChange={(e) =>
                          applyFilter("searchTerm", e.target.value)
                        }
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FontAwesomeIcon
                          icon={faSearch}
                          className="text-gray-400"
                        />
                      </div>
                      {filters.searchTerm && (
                        <button
                          onClick={() => applyFilter("searchTerm", "")}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Active filters display */}
        {hasActiveFilters && (
          <div className="mb-6 flex flex-wrap gap-2">
            {filters.customerIds.map((id) => {
              const customerOption = customerOptions.find(
                (c) => c.value === id
              );
              return (
                <span
                  key={id}
                  className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
                >
                  <span className="font-medium mr-1">Customer:</span>{" "}
                  {customerOption?.label || id}
                  <button
                    onClick={() => {
                      applyFilter(
                        "customerIds",
                        filters.customerIds.filter((cid) => cid !== id)
                      );
                    }}
                    className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                </span>
              );
            })}

            {filters.status.map((status) => (
              <span
                key={status}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Status:</span> {status}
                <button
                  onClick={() => {
                    applyFilter(
                      "status",
                      filters.status.filter((s) => s !== status)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {filters.transportTypes.map((type) => (
              <span
                key={type}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200"
              >
                <span className="font-medium mr-1">Transport:</span> {type}
                <button
                  onClick={() => {
                    applyFilter(
                      "transportTypes",
                      filters.transportTypes.filter((t) => t !== type)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {filters.searchTerm && (
              <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100 border border-blue-200">
                <span className="font-medium mr-1">Search:</span>{" "}
                {filters.searchTerm}
                <button
                  onClick={() => applyFilter("searchTerm", "")}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border border-gray-200 flex flex-col">
            <div className="flex items-start">
              <div className="bg-blue-100 p-3 rounded-full">
                <FontAwesomeIcon
                  icon={faLayerGroup}
                  className="text-blue-600"
                />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">
                  Total Shipments
                </h3>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200 flex flex-col">
            <div className="flex items-start">
              <div className="bg-emerald-100 p-3 rounded-full">
                <FontAwesomeIcon icon={faCheck} className="text-emerald-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">Completed</h3>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.completed || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200 flex flex-col">
            <div className="flex items-start">
              <div className="bg-green-100 p-3 rounded-full">
                <FontAwesomeIcon icon={faCheck} className="text-green-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">On Time</h3>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.onTime}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200 flex flex-col">
            <div className="flex items-start">
              <div className="bg-amber-100 p-3 rounded-full">
                <FontAwesomeIcon icon={faClock} className="text-amber-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">Delayed</h3>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.delayed}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200 flex flex-col">
            <div className="flex items-start">
              <div className="bg-red-100 p-3 rounded-full">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-red-600"
                />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">Critical</h3>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.critical}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 border border-gray-200 flex flex-col">
            <div className="flex items-start">
              <div className="bg-blue-100 p-3 rounded-full">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="text-blue-600"
                />
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">
                  Upcoming (7 days)
                </h3>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.upcoming}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className={`grid ${chartSizeClasses.columns} gap-6 mb-6`}>
          {/* Transport Type Distribution */}
          <div
            className="bg-white shadow rounded-lg p-4 border border-gray-200 flex flex-col"
            style={{
              height:
                chartLayout.size === "small"
                  ? "320px"
                  : chartLayout.size === "medium"
                  ? "400px"
                  : "500px",
            }}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <FontAwesomeIcon icon={faShip} className="text-blue-600 mr-2" />
                Transport Type Distribution
              </h3>
            </div>
            <div className="flex-grow overflow-hidden">
              <TransportTypeDistributionChart data={stats.byTransportType} />
            </div>
          </div>

          {/* Status Distribution */}
          <div
            className="bg-white shadow rounded-lg p-4 border border-gray-200 flex flex-col"
            style={{
              height:
                chartLayout.size === "small"
                  ? "320px"
                  : chartLayout.size === "medium"
                  ? "400px"
                  : "500px",
            }}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <FontAwesomeIcon icon={faTag} className="text-blue-600 mr-2" />
                Status Distribution
              </h3>
            </div>
            <div className="flex-grow overflow-hidden">
              <StatusDistributionChart
                data={filteredData || []}
                onItemClick={(status) => {
                  // Add the clicked status to filters if not already there
                  if (!filters.status.includes(status)) {
                    applyFilter("status", [...filters.status, status]);
                  }
                }}
              />
            </div>
          </div>

          {/* Shipments by Weekday */}
          <div
            className="bg-white shadow rounded-lg p-4 border border-gray-200 flex flex-col"
            style={{
              height:
                chartLayout.size === "small"
                  ? "320px"
                  : chartLayout.size === "medium"
                  ? "400px"
                  : "500px",
            }}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <FontAwesomeIcon
                  icon={faChartBar}
                  className="text-blue-600 mr-2"
                />
                Shipments by Weekday
              </h3>
            </div>
            <div className="flex-grow overflow-hidden">
              <ShipmentsByWeekdayChart data={stats.byWeekday} />
            </div>
          </div>
        </div>

        {/* Legends */}
        <div className="mb-6 bg-white p-3 rounded-lg shadow border border-gray-200">
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-50 border mr-2"></div>
              <span>On Time</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-blue-100 border mr-2"></div>
              <span>Upcoming</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-amber-100 border mr-2"></div>
              <span>Delayed</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-red-100 border mr-2"></div>
              <span>Critical</span>
            </div>
          </div>
        </div>

        {/* Calendar View */}
        <div className="mb-6">
          {viewType === "month" && (
            <CalendarMonthView
              calendarData={calendarData}
              selectedDate={selectedDate}
              isSameDay={isSameDay}
              getFirstDayOfMonth={getFirstDayOfMonth}
              getLastDayOfMonth={getLastDayOfMonth}
              getShipmentBackgroundColor={getShipmentBackgroundColor}
              getShipmentStatusText={getShipmentStatusText}
              getTransportIcon={getTransportIcon}
              handleShipmentClick={handleShipmentClick}
              setHoveredReference={setHoveredReference}
              hoveredReference={hoveredReference}
            />
          )}

          {viewType === "week" && (
            <CalendarWeekView
              calendarData={calendarData}
              selectedDate={selectedDate}
              isSameDay={isSameDay}
              getFirstDayOfWeek={getFirstDayOfWeek}
              getShipmentBackgroundColor={getShipmentBackgroundColor}
              getShipmentStatusText={getShipmentStatusText}
              getTransportIcon={getTransportIcon}
              handleShipmentClick={handleShipmentClick}
              isShipmentCritical={isShipmentCritical}
              isShipmentDelayed={isShipmentDelayed}
              isShipmentUpcoming={isShipmentUpcoming}
            />
          )}

          {viewType === "day" && (
            <CalendarDayView
              calendarData={calendarData}
              selectedDate={selectedDate}
              getShipmentBackgroundColor={getShipmentBackgroundColor}
              getShipmentStatusText={getShipmentStatusText}
              getTransportIcon={getTransportIcon}
              handleShipmentClick={handleShipmentClick}
              isShipmentCritical={isShipmentCritical}
              isShipmentDelayed={isShipmentDelayed}
              isShipmentUpcoming={isShipmentUpcoming}
              getEtaDate={getEtaDate}
            />
          )}
        </div>
      </div>

      {/* Shipment Detail Modal */}
      {showDetailModal && selectedReference && (
        <ShipmentDetailModal
          item={selectedReference}
          onClose={() => setShowDetailModal(false)}
          getTransportIcon={getTransportIcon}
          getEtaDate={getEtaDate}
          getShipmentStatusText={getShipmentStatusText}
          formatDate={formatDate}
          isShipmentDelayed={isShipmentDelayed}
          isShipmentCritical={isShipmentCritical}
          isShipmentClosed={isShipmentClosed}
        />
      )}
    </div>
  );
};

export default EnhancedTrafficScheduler;
